<template>
  <div class="financial-data-account-modal">
    <bp-radio
      v-model="form.type"
      class="mb-5"
      :vertical="isMobile"
      :options="[
        {
          id: 'pf',
          name: 'Pessoa Física',
          description: 'Limite de saques: R$ 1.900,00/mês'
        },
        {
          id: 'pj',
          name: 'Pessoa Jurídica',
          description: 'Saque SEM limitação de valor'
        }
      ]"
    />

    <div
      class="p-3 mb-5 border rounded border-information-400 bg-information-024"
    >
      <div class="d-flex gap-3">
        <bp-icon icon="fa fa-info-circle" class="text-information-500" />
        <span class="functional-1 text-information-800">
          Certifique-se de que o {{ documentType }} da conta bancária seja o
          mesmo {{ documentType }} que está cadastrado em "Dados pessoais"
        </span>
      </div>
    </div>

    <bp-select
      v-model="form.bank_id"
      required
      class="mb-5"
      label="Banco"
      :options="banksOptions"
    />

    <div class="row">
      <div class="col-lg-3 mb-5">
        <bp-input
          v-model="form.agency"
          required
          label="Agência"
          placeholder="0000"
        />
      </div>
      <div class="col-lg-3 mb-5">
        <bp-input
          v-model="form.agency_vd"
          mask="#"
          label="Dígito"
          placeholder="0"
        />
      </div>
      <div class="col-lg-3 mb-5">
        <bp-input
          v-model="form.number_account"
          required
          label="Conta"
          placeholder="0000000"
        />
      </div>
      <div class="col-lg-3 mb-5">
        <bp-input
          v-model="form.number_account_vd"
          mask="#"
          label="Dígito"
          placeholder="0"
        />
      </div>
    </div>

    <div class="row">
      <div class="col-lg-6 mb-5">
        <bp-input
          v-model="form.owner_account"
          required
          label="Titular da conta"
          placeholder="Ex. Seu Nome"
        />
      </div>
      <div class="col-lg-6 mb-5">
        <bp-input
          v-if="form.type == 'pf'"
          v-model="form.document"
          required
          label="CPF"
          mask="###.###.###-##"
          placeholder="000.000.000-00"
          :errors="errors.document"
        />
        <bp-input
          v-else
          required
          label="CNPJ"
          v-model="form.document"
          mask="##.###.###/####-##"
          placeholder="00.000.000/0000-00"
          :errors="errors.document"
        />
      </div>
    </div>

    <bp-select
      v-model="form.type_account"
      class="mb-5"
      label="Tipo de conta"
      :options="[
        { id: 1, name: 'Conta Corrente' },
        { id: 2, name: 'Conta Poupança' }
      ]"
      :errors="errors.type_account"
    />
    <div>
      Comprovante do seu CPF/CNPJ <span class="text-negative-700">*</span>
    </div>
    <bp-input-file class="mt-3 w-100" v-model="form.document_file" />
    <div
      class="d-flex flex-column flex-lg-row mt-7 justify-content-lg-end gap-5"
    >
      <bp-button
        label="Salvar"
        class="order-lg-1"
        :loading="loading"
        :disabled="!isValid"
        @click="submit"
      />
      <bp-button
        label="Cancelar"
        color="neutral"
        variant="filled-1"
        @click="$emit('close')"
      />
    </div>
  </div>
</template>

<script>
import { formFields, isValidCNPJ, isValidCPF } from "@/functions/helpers";
import { mapState } from "vuex";
export default {
  data() {
    return {
      ...formFields(
        [
          "id",
          "type",
          "bank_id",
          "agency",
          "agency_vd",
          "number_account",
          "number_account_vd",
          "owner_account",
          "document",
          "type_account",
          "document_file"
        ],
        { type: "pf", bank_id: 1 }
      ),
      loading: false,
      isMobile: window.innerWidth < 992
    };
  },
  computed: {
    ...mapState("financial", ["banksList"]),
    documentType() {
      return this.form.type == "pf" ? "CPF" : "CNPJ";
    },
    banksOptions() {
      return this.banksList.map(item => {
        return {
          id: item.id,
          name: `${item.compe} - ${item.long_name}`
        };
      });
    },
    isValid() {
      if (!this.form.document) return false;

      if (
        (this.form.type == "pj" && !isValidCNPJ(this.form.document)) ||
        (this.form.type == "pf" && !isValidCPF(this.form.document))
      ) {
        return false;
      }

      return !!(
        this.form.type &&
        this.form.bank_id &&
        this.form.agency &&
        this.form.number_account &&
        this.form.owner_account &&
        this.form.type_account &&
        this.form.document_file
      );
    }
  },
  methods: {
    submit() {
      const data = { ...this.form };
      data.agency_vd = data.agency_vd || 0;
      data.number_account_vd = data.number_account_vd || 0;

      this.loading = true;
      this.$store
        .dispatch("financial/postBankAccount", data)
        .then(() => {
          this.$message.success("Conta bancária cadastrada com sucesso");
          this.$emit("close");
          this.$emit("refresh");
        })
        .catch(() => {
          this.$message.error("Erro ao cadastrar conta bancária");
        })
        .finally(() => {
          this.loading = false;
        });
    }
  },
  watch: {
    "form.type"() {
      this.form.document = "";
    }
  }
};
</script>

<style lang="scss" scoped>
.financial-data-account-modal {
  ::v-deep {
    .input-container {
      aspect-ratio: 6;
    }

    .bp-radio__option-wrapper {
      transition: 0.2s all;
      flex: 1;
      padding: 16px;
      border-radius: 6px;
      backdrop-filter: blur(2px);
      border: 1px solid var(--Semantic-Neutral-100, #d7d7d7);
      background: var(--Surface-Cumulative-plus8, rgba(255, 255, 255, 0.32));

      &:has(input:checked) {
        border-radius: 6px;
        box-shadow: 0px 0px 20px 0px rgba(0, 0, 0, 0.12);
        border: 1px solid var(--Semantic-Primary-Accent-200, #b983ff);
        background: var(--Surface-Cumulative-plus20, rgba(255, 255, 255, 0.8));
      }
    }
  }
}
</style>
